import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";import { toast } from "sonner";
import { ArrowBigUp, Plus, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { ASSETS } from "@/lib/assets";
import { useAuth } from "@/context/AuthContext";
import BadgeTag from "@/components/BadgeTag";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { v: "meme", l: "Meme" },
  { v: "sold_bottom", l: "Sold The Bottom" },
  { v: "bought_top", l: "Bought The Top" },
  { v: "rug", l: "Got Rugged" },
  { v: "missed_pump", l: "Missed The Pump" },
  { v: "bad_advice", l: "Bad Advice" },
  { v: "wallet_disaster", l: "Wallet Disaster" },
  { v: "held_too_long", l: "Held Too Long" },
  { v: "trading_disaster", l: "Trading Disaster" },
];

const catLabel = (v) => CATEGORIES.find((c) => c.v === v)?.l || "Robbed";

export default function RobbedWall() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sort, setSort] = useState("top");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", story: "", category: "trading_disaster" });

  const { data: stories = [], isLoading } = useQuery({
    queryKey: ["stories", sort],
    queryFn: async () => (await api.get(`/stories?sort=${sort}`)).data,
  });

  const requireAuth = () => {
    if (!user) { toast.error("Log in to join the Robbed."); navigate("/login"); return false; }
    return true;
  };

  const upvote = useMutation({
    mutationFn: (id) => api.post(`/stories/${id}/upvote`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["stories"] }); toast.success("Respect. +1"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const submit = useMutation({
    mutationFn: () => api.post("/stories", form),
    onSuccess: () => {
      toast.success("Story submitted — pending moderation. Welcome to the Robbed.");
      setOpen(false);
      setForm({ username: "", story: "", category: "trading_disaster" });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const onConfess = () => { if (requireAuth()) setOpen(true); };
  const onUpvote = (id) => { if (requireAuth()) upvote.mutate(id); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <span className="tag">The Support Group</span>
          <h1 className="headline text-cream text-4xl sm:text-6xl mt-5">WHAT <span className="text-neon">ROBBED</span> YOU?</h1>
          <p className="font-mono text-dim mt-4 max-w-xl">Post your worst trade. Upvote the most tragic. We heal through memes.</p>
        </div>

        <div className="flex items-center gap-3">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 bg-ink2 border-3 border-black text-cream font-mono uppercase text-xs" data-testid="wall-sort">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink2 border-3 border-black text-cream">
              <SelectItem value="top">Top Voted</SelectItem>
              <SelectItem value="recent">Most Recent</SelectItem>
            </SelectContent>
          </Select>

          <button onClick={onConfess} className="btn-neon !py-2.5 !px-4 !text-xs" data-testid="wall-submit-open"><Plus className="w-4 h-4" /> Confess</button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="bg-ink2 border-3 border-black text-cream max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display font-900 uppercase text-neon">Confess Your Damage</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <input
                  data-testid="story-username"
                  className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon"
                  placeholder="Display name (optional)"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                />
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="w-full bg-ink border-3 border-black text-cream font-mono text-sm" data-testid="story-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ink2 border-3 border-black text-cream">
                    {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <textarea
                  data-testid="story-text"
                  rows={4}
                  className="w-full bg-ink border-3 border-black px-3 py-2.5 font-mono text-sm text-cream focus:outline-none focus:border-neon resize-none"
                  placeholder="How did the market rob you?"
                  value={form.story}
                  onChange={(e) => setForm({ ...form, story: e.target.value })}
                />
                <button
                  data-testid="story-submit"
                  disabled={submit.isPending}
                  onClick={() => submit.mutate()}
                  className="btn-neon w-full disabled:opacity-50"
                >
                  {submit.isPending ? "Submitting..." : "Submit To The Wall"}
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center py-24">
          <img src={ASSETS.shrugging} alt="loading" className="w-32 animate-floaty" />
          <p className="font-display text-neon mt-4 uppercase">Counting the damage...</p>
        </div>
      ) : stories.length === 0 ? (
        <div className="flex flex-col items-center py-24">
          <img src={ASSETS.shrugging} alt="empty" className="w-40" />
          <p className="font-display text-cream mt-4 uppercase">Nobody's confessed yet. Be the first to get Robbed.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          <AnimatePresence>
            {stories.map((s, i) => (
              <motion.div
                key={s.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className="comic-card p-5 flex flex-col"
                data-testid="story-card"
              >
                <div className="flex items-center justify-between">
                  <span className="tag !text-[10px] !px-2 !py-0.5">{catLabel(s.category)}</span>
                  <span className="flex items-center gap-1.5 min-w-0">
                    {s.user_id
                      ? <Link to={`/u/${s.user_id}`} className="font-mono text-[10px] text-dim hover:text-neon truncate">@{s.username}</Link>
                      : <span className="font-mono text-[10px] text-dim truncate">@{s.username}</span>}
                    <BadgeTag rob={s.rob} />
                  </span>
                </div>
                {s.image_url && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${s.image_url}`}
                    alt="ROBBED meme"
                    loading="lazy"
                    className="mt-3 w-full max-h-72 object-contain border-3 border-black bg-white"
                    data-testid={`story-image-${s.id}`}
                  />
                )}
                {s.story && <p className="font-mono text-sm text-cream/90 mt-4 flex-1 leading-relaxed">"{s.story}"</p>}
                <button
                  onClick={() => onUpvote(s.id)}
                  className="mt-4 flex items-center gap-2 self-start border-3 border-black bg-ink3 hover:bg-neon hover:text-black text-neon font-display font-900 px-3 py-1.5 transition-colors"
                  data-testid={`upvote-${s.id}`}
                >
                  <ArrowBigUp className="w-5 h-5" /> {s.upvotes}
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
